import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  CategoryPresets,
  CategorySegment,
  computeAutomaticResult,
  computeTargetRoasResult,
  DEFAULT_DISTRESSED_DISCOUNTS,
  getCategorySegment,
  PresetDataPoint,
} from 'src/utils/strategyCalculator';
import { TopLevelCategory } from 'src/WebApiController';

/**
 * Fetches preset curves for a single segment from the backend API.
 * The response contains both priceTrend and salesTiming curves.
 */
const fetchPresetCurves = async (segment: string): Promise<CategoryPresets> => {
  const response = await fetch(
    `/api/StrategyCalculator/GetPresetCurves?segment=${encodeURIComponent(segment)}`
  );
  if (!response.ok) {
    throw new Error(
      `Failed to fetch preset curves for segment '${segment}': ${response.status}`
    );
  }
  return response.json();
};

/**
 * Loads preset curve data for a given category segment.
 * Fetches from the backend API and caches via React Query.
 */
export const usePresetData = (
  segment: CategorySegment
): {
  priceTrendPresets: CategoryPresets | undefined;
  salesTimingPresets: CategoryPresets | undefined;
  isLoading: boolean;
} => {
  const query = useQuery({
    queryKey: ['strategyCalculator', 'presetCurves', segment],
    queryFn: () => fetchPresetCurves(segment),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // The API returns a single object with both priceTrend and salesTiming.
  // We split it into two CategoryPresets to keep the consumer interface stable.
  const priceTrendPresets = useMemo(
    (): CategoryPresets | undefined =>
      query.data
        ? {
            segment: query.data.segment,
            priceTrend: query.data.priceTrend,
            salesTiming: [],
          }
        : undefined,
    [query.data]
  );

  const salesTimingPresets = useMemo(
    (): CategoryPresets | undefined =>
      query.data
        ? {
            segment: query.data.segment,
            priceTrend: [],
            salesTiming: query.data.salesTiming,
          }
        : undefined,
    [query.data]
  );

  return {
    priceTrendPresets,
    salesTimingPresets,
    isLoading: query.isLoading,
  };
};

const computeDaysToEvent = (eventDate: Date): number => {
  const now = new Date();
  const diffMs = eventDate.getTime() - now.getTime();
  return Math.max(0, Math.round(diffMs / (24 * 60 * 60 * 1000)));
};

/**
 * Rebase a price curve so that the value at `daysToEvent` (today) = 1.0.
 * The Snowflake data uses absolute price ratios; the engine expects values
 * relative to today's price.
 */
const rebasePriceCurve = (
  data: PresetDataPoint[],
  daysToEvent: number
): PresetDataPoint[] => {
  if (data.length === 0) return data;

  const sorted = [...data].sort((a, b) => a.daysToEvent - b.daysToEvent);
  let todayValue: number;

  const exact = sorted.find((p) => p.daysToEvent === daysToEvent);
  if (exact) {
    todayValue = exact.value;
  } else {
    if (daysToEvent <= sorted[0].daysToEvent) {
      todayValue = sorted[0].value;
    } else if (daysToEvent >= sorted[sorted.length - 1].daysToEvent) {
      todayValue = sorted[sorted.length - 1].value;
    } else {
      todayValue = sorted[0].value;
      for (let i = 0; i < sorted.length - 1; i++) {
        if (
          daysToEvent >= sorted[i].daysToEvent &&
          daysToEvent <= sorted[i + 1].daysToEvent
        ) {
          const t =
            (daysToEvent - sorted[i].daysToEvent) /
            (sorted[i + 1].daysToEvent - sorted[i].daysToEvent);
          todayValue =
            sorted[i].value + t * (sorted[i + 1].value - sorted[i].value);
          break;
        }
      }
    }
  }

  if (todayValue <= 0 || todayValue === 1) return data;

  return data.map((p) => ({
    daysToEvent: p.daysToEvent,
    value: p.value / todayValue,
  }));
};

export interface DefaultRecommendation {
  /** For TargetRoas mode: the recommended ROAS value */
  optimalRoas: number | null;
  /** For Automatic mode: the recommended strategy display name */
  recommendedStrategy: string | null;
  /** For Automatic mode: the recommended strategy key (matches SpendStrategy enum) */
  recommendedStrategyKey: string | null;
  /** Whether the recommendation suggests NOT boosting */
  isNegativeIncremental: boolean;
  /** Whether preset data is still loading */
  isLoading: boolean;
}

/**
 * Computes a default recommendation using p50 presets (no user customization).
 * Used to show an inline recommendation on the campaign form before the user
 * opens the full Strategy Calculator.
 */
export const useDefaultRecommendation = ({
  genre,
  parentCategoryId,
  eventDate,
  inventoryValue,
  bidMode,
}: {
  genre?: TopLevelCategory;
  parentCategoryId?: number;
  eventDate: Date;
  inventoryValue: number;
  bidMode: 'TargetRoas' | 'Automatic';
}): DefaultRecommendation => {
  const segment = useMemo(
    () => getCategorySegment(genre, parentCategoryId),
    [genre, parentCategoryId]
  );
  const { priceTrendPresets, salesTimingPresets, isLoading } =
    usePresetData(segment);

  return useMemo(() => {
    if (isLoading || !priceTrendPresets || !salesTimingPresets) {
      return {
        optimalRoas: null,
        recommendedStrategy: null,
        recommendedStrategyKey: null,
        isNegativeIncremental: false,
        isLoading,
      };
    }

    const p50Price = priceTrendPresets.priceTrend.find(
      (p) => p.percentile === 'p50'
    );
    const p50Sales = salesTimingPresets.salesTiming.find(
      (p) => p.percentile === 'p50'
    );

    if (!p50Price || !p50Sales) {
      return {
        optimalRoas: null,
        recommendedStrategy: null,
        recommendedStrategyKey: null,
        isNegativeIncremental: false,
        isLoading: false,
      };
    }

    const daysToEvent = computeDaysToEvent(eventDate);

    const rebasedPriceCurve = rebasePriceCurve(
      p50Price.data as PresetDataPoint[],
      daysToEvent
    );

    const inputs = {
      priceCurve: rebasedPriceCurve,
      salesTimingCurve: p50Sales.data as PresetDataPoint[],
      unsoldPct: 0,
      distressedDiscounts: { ...DEFAULT_DISTRESSED_DISCOUNTS },
      inventoryValue,
      daysToEvent,
    };

    if (bidMode === 'TargetRoas') {
      const result = computeTargetRoasResult(inputs);
      return {
        optimalRoas: result.optimalRoas,
        recommendedStrategy: null,
        recommendedStrategyKey: null,
        isNegativeIncremental: result.isNegativeIncremental,
        isLoading: false,
      };
    }

    const result = computeAutomaticResult(inputs);
    return {
      optimalRoas: result.optimalRoas,
      recommendedStrategy: result.recommendedStrategy?.name ?? null,
      recommendedStrategyKey: result.recommendedStrategy?.key ?? null,
      isNegativeIncremental: result.isNegativeIncremental,
      isLoading: false,
    };
  }, [
    isLoading,
    priceTrendPresets,
    salesTimingPresets,
    eventDate,
    inventoryValue,
    bidMode,
  ]);
};
